import React, { useState, useEffect } from 'react';
// Inline text utility functions to avoid import issues
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const anyTextContains = (texts: (string | undefined | null)[], searchTerm: string): boolean => {
  const normalizedSearch = normalizeText(searchTerm);
  return texts.some(text => text && normalizeText(text).includes(normalizedSearch));
};

interface Report {
  id: number;
  station_id: number;
  fuel_type: string;
  status: string;
  price?: number;
  comment?: string;
  created_at: string;
}

interface Station {
  id: number;
  name: string;
  brand: string;
  address: string;
  quartier?: string;
  commune: string;
  latitude: number;
  longitude: number;
  latest_report?: {
    id?: number;
    fuel_type: string;
    status: string;
    price?: number;
    comment?: string;
    created_at: string;
  };
}

interface QuickReportModalProps {
  stations: Station[];
  selectedStation?: Station | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const QuickReportModal: React.FC<QuickReportModalProps> = ({
  stations,
  selectedStation: preselectedStation,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selectedStationId, setSelectedStationId] = useState<number | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchMode, setSearchMode] = useState<'commune' | 'direct'>('direct');
  const [formData, setFormData] = useState({
    fuel_type: 'essence' as 'essence' | 'diesel' | 'both',
    status: 'available' as 'available' | 'low' | 'out',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [actionMode, setActionMode] = useState<'new_report' | 'confirm' | 'contest'>('new_report');
  const [recentReports, setRecentReports] = useState<Report[]>([]);
  const [showAllReports, setShowAllReports] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);

  // Obtenir les quartiers uniques
  const communes = [...new Set(stations.map(s => s.commune))].sort();

  // Filtrer les stations selon le mode de recherche
  const filteredStations = searchMode === 'commune'
    ? (selectedDistrict ? stations.filter(s => s.commune === selectedDistrict) : [])
    : searchTerm
      ? stations.filter(station =>
          anyTextContains([station.name, station.brand, station.address, station.commune, station.quartier], searchTerm)
        )
      : stations;

  const selectedStation = stations.find(s => s.id === selectedStationId);

  // Fonction pour récupérer les rapports récents d'une station
  const fetchRecentReports = async (stationId: number) => {
    setLoadingReports(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/stations/${stationId}/reports?limit=10`);
      if (response.ok) {
        const reports = await response.json();
        setRecentReports(reports);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rapports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Réinitialiser les états quand le modal s'ouvre OU quand la station change
  useEffect(() => {
    if (isOpen) {
      setActionMode('new_report');
      setError(null);
      setSuccess(false);

      // Seulement réinitialiser la recherche si pas de station pré-sélectionnée
      if (!preselectedStation) {
        setSearchTerm('');
        setSelectedDistrict('');
        setSelectedStationId(null);
      }
    }
  }, [isOpen, preselectedStation]); // Reset when modal opens OR when station changes

  // Charger les rapports quand une station est sélectionnée
  useEffect(() => {
    const currentStationId = preselectedStation ? preselectedStation.id : selectedStationId;
    if (currentStationId) {
      fetchRecentReports(currentStationId);
    } else {
      setRecentReports([]);
    }
  }, [selectedStationId, preselectedStation]);

  // Reset status to 'available' if it was set to 'out' (security measure)
  useEffect(() => {
    if (formData.status === 'out') {
      setFormData(prev => ({ ...prev, status: 'available' }));
    }
  }, [formData.status]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const stationId = preselectedStation ? preselectedStation.id : selectedStationId;
    const currentStation = preselectedStation || selectedStation;

    if (!stationId) {
      setError('Veuillez sélectionner une station');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (actionMode === 'confirm' || actionMode === 'contest') {
        // Mode vote : confirmer ou contester le rapport existant
        if (!currentStation?.latest_report?.id) {
          throw new Error('Aucun rapport à voter');
        }

        const voteData = {
          report_id: currentStation.latest_report.id,
          vote_type: actionMode,
          comment: formData.comment || null
        };

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/reports/${currentStation.latest_report.id}/vote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(voteData)
        });

        if (!response.ok) {
          throw new Error('Erreur lors de l\'envoi du vote');
        }

      } else {
        // Mode nouveau signalement
        const reportData = {
          station_id: stationId,
          fuel_type: formData.fuel_type,
          status: formData.status,
          comment: formData.comment || undefined
        };

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/reports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(reportData)
        });

        if (!response.ok) {
          throw new Error('Erreur lors de l\'envoi du signalement');
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
        resetForm();
      }, 1500);

    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedStationId(null);
    setSelectedDistrict('');
    setActionMode('new_report');
    setFormData({
      fuel_type: 'essence',
      status: 'available',
      comment: ''
    });
    setError(null);
    setSuccess(false);
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
            resetForm();
          }
        }}
      >
        <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-xs sm:max-w-md w-full p-4 sm:p-6 text-center sm:mx-4">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Signalement envoyé !
          </h2>
          <p className="text-gray-600">
            Merci pour votre contribution à la communauté BamakoFuel !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
          resetForm();
        }
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-md sm:max-w-2xl max-h-[90vh] overflow-y-auto sm:mx-4">
        <div className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <h2 className="text-sm sm:text-lg font-semibold text-gray-900 pr-2 leading-tight">
              📝 Vous savez où se trouve du carburant? Déclarez-le!
            </h2>
            <button
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 text-lg sm:text-xl flex-shrink-0 w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {error}
            </div>
          )}


          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
            {/* Sélection de station uniquement si pas de station pré-sélectionnée */}
            {!preselectedStation && (
              <>
                {/* Mode de recherche */}
                <div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode('direct');
                        setSelectedDistrict('');
                        setSearchTerm('');
                        setSelectedStationId(null);
                      }}
                      className={`flex-1 py-2 px-2 rounded text-xs font-medium ${
                        searchMode === 'direct'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      🎯 Recherche
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode('commune');
                        setSearchTerm('');
                        setSelectedStationId(null);
                      }}
                      className={`flex-1 py-2 px-2 rounded text-xs font-medium ${
                        searchMode === 'commune'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      🏢 Commune
                    </button>
                  </div>
                </div>

                {searchMode === 'direct' ? (
                  /* Recherche directe de station */
                  <div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setSelectedStationId(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Nom, marque ou adresse..."
                    />

                    {searchTerm && filteredStations.length > 0 && (
                      <div className="mt-1 max-h-32 overflow-y-auto border border-gray-200 rounded bg-white">
                        {filteredStations.slice(0, 6).map(station => (
                          <button
                            key={station.id}
                            type="button"
                            onClick={() => setSelectedStationId(station.id)}
                            className={`w-full text-left px-2 py-1.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                              selectedStationId === station.id ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900 text-sm">{station.name}</div>
                            <div className="text-xs text-gray-600">{station.brand} • {station.commune}</div>
                          </button>
                        ))}
                        {filteredStations.length > 6 && (
                          <div className="px-2 py-1 text-xs text-gray-500 text-center bg-gray-50">
                            +{filteredStations.length - 6} autres...
                          </div>
                        )}
                      </div>
                    )}

                    {searchTerm && filteredStations.length === 0 && (
                      <div className="mt-2 p-3 text-sm text-gray-500 bg-gray-50 rounded-md text-center">
                        Aucune station trouvée. Essayez un autre terme de recherche.
                      </div>
                    )}
                  </div>
                ) : (
                  /* Sélection par quartier */
                  <>
                    <div>
                      <select
                        value={selectedDistrict}
                        onChange={(e) => {
                          setSelectedDistrict(e.target.value);
                          setSelectedStationId(null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        required
                      >
                        <option value="">🏢 Sélectionnez une commune</option>
                        {communes.map(commune => (
                          <option key={commune} value={commune}>{commune}</option>
                        ))}
                      </select>
                    </div>

                    {selectedDistrict && (
                      <div>
                        <select
                          value={selectedStationId || ''}
                          onChange={(e) => setSelectedStationId(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        >
                          <option value="">⛽ Sélectionnez une station</option>
                          {filteredStations.map(station => (
                            <option key={station.id} value={station.id}>
                              {station.name} - {station.brand}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Aperçu de la station sélectionnée */}
            {(preselectedStation || selectedStation) && (
              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                <div className="font-medium text-gray-900 text-sm">{(preselectedStation || selectedStation)?.name}</div>
                <div className="text-xs text-gray-600">{(preselectedStation || selectedStation)?.brand} • {(preselectedStation || selectedStation)?.commune}</div>
              </div>
            )}

            {/* Statut actuel et options d'action */}
            {(preselectedStation || selectedStation) && (preselectedStation || selectedStation)?.latest_report && (
              <div className="bg-gray-50 p-2 rounded border">
                <div className="bg-white p-2 rounded mb-2">
                  <div className="flex justify-between items-center text-xs">
                    <span>
                      🛢️ {(preselectedStation || selectedStation)?.latest_report?.fuel_type === 'essence' ? 'Essence' :
                          (preselectedStation || selectedStation)?.latest_report?.fuel_type === 'diesel' ? 'Diesel' : 'Les deux'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      (preselectedStation || selectedStation)?.latest_report?.status === 'available' ? 'bg-green-500 text-white' :
                      (preselectedStation || selectedStation)?.latest_report?.status === 'low' ? 'bg-orange-500 text-white' :
                      (preselectedStation || selectedStation)?.latest_report?.status === 'out' ? 'bg-red-500 text-white' :
                      'bg-gray-500 text-white'
                    }`}>
                      {(preselectedStation || selectedStation)?.latest_report?.status === 'available' ? '✅' :
                       (preselectedStation || selectedStation)?.latest_report?.status === 'low' ? '⚠️' :
                       (preselectedStation || selectedStation)?.latest_report?.status === 'out' ? '❌' : '?'}
                    </span>
                  </div>
                  {(preselectedStation || selectedStation)?.latest_report?.comment && (
                    <div className="text-xs text-gray-600 mt-1 bg-gray-50 p-1 rounded">
                      💬 {(preselectedStation || selectedStation)?.latest_report?.comment}
                    </div>
                  )}

                  {/* Votes/Contestations display */}
                  {((preselectedStation || selectedStation)?.latest_report?.confirm_count || 0) > 0 || ((preselectedStation || selectedStation)?.latest_report?.contest_count || 0) > 0 ? (
                    <div className="text-xs mt-1 bg-gray-50 p-2 rounded space-y-1">
                      {((preselectedStation || selectedStation)?.latest_report?.confirm_count || 0) > 0 && (
                        <div className="text-green-600">
                          ✅ {(preselectedStation || selectedStation)?.latest_report?.confirm_count} confirmations
                        </div>
                      )}
                      {((preselectedStation || selectedStation)?.latest_report?.contest_count || 0) > 0 && (
                        <div className="text-red-600 font-medium">
                          ❌ {(preselectedStation || selectedStation)?.latest_report?.contest_count} contestations
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="text-xs text-gray-500 mt-1">
                    🕒 Mis à jour {new Date((preselectedStation || selectedStation)?.latest_report?.created_at || '').toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                <div className="mb-2">
                  <div className="text-xs text-gray-600 mb-2 text-center">
                    👆 Votez sur cette info ou créez votre propre signalement
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setActionMode('confirm')}
                      className={`p-2 rounded text-xs border ${
                        actionMode === 'confirm'
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : 'bg-white border-gray-200 text-gray-700'
                      }`}
                    >
                      ✅ Confirmer
                    </button>
                    <button
                      type="button"
                      onClick={() => setActionMode('contest')}
                      className={`p-2 rounded text-xs border ${
                        actionMode === 'contest'
                          ? 'bg-red-100 border-red-300 text-red-800'
                          : 'bg-white border-gray-200 text-gray-700'
                      }`}
                    >
                      ❌ Contester
                    </button>
                    <button
                      type="button"
                      onClick={() => setActionMode('new_report')}
                      className={`p-2 rounded text-xs border ${
                        actionMode === 'new_report'
                          ? 'bg-blue-100 border-blue-300 text-blue-800'
                          : 'bg-white border-gray-200 text-gray-700'
                      }`}
                    >
                      📝 Nouveau
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Section des commentaires récents */}
            {(preselectedStation || selectedStation) && recentReports.length > 0 && (
              <div className="bg-gray-50 p-2 rounded border">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-xs font-medium text-gray-700 flex items-center">
                    💬 Activité récente
                  </h4>
                  {recentReports.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllReports(!showAllReports)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {showAllReports ? 'Voir moins' : 'Voir activité récente'}
                    </button>
                  )}
                </div>

                {showAllReports && (
                  <div className="space-y-1 mt-2">
                    {recentReports.map((report) => (
                      <div key={report.id} className="bg-white p-2 rounded text-xs border border-gray-100">
                        {report.comment && (
                          <div className="text-gray-700 mb-1">
                            💬 {report.comment}
                          </div>
                        )}
                        <div className="text-gray-500 text-xs">
                          🕒 {new Date(report.created_at).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {loadingReports && (
                  <div className="text-center text-xs text-gray-500 py-2">
                    ⏳ Chargement...
                  </div>
                )}
              </div>
            )}

            {/* Champs pour nouveau signalement */}
            {actionMode === 'new_report' && (
              <div className="space-y-2">
                <select
                  value={formData.fuel_type}
                  onChange={(e) => setFormData({...formData, fuel_type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="essence">🛢️ Essence</option>
                  <option value="diesel">🛢️ Diesel</option>
                  <option value="both">🛢️ Les deux</option>
                </select>

                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="available">✅ Disponible</option>
                  <option value="out" disabled className="text-gray-400">❌ Rupture de stock (Non disponible pour les utilisateurs)</option>
                </select>

              </div>
            )}

            {/* Commentaire - Version compacte */}
            <div>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={2}
                placeholder="💬 Commentaire (optionnel)"
                maxLength={200}
              />
            </div>

            {/* Buttons - Version compacte */}
            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-3 rounded text-sm"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={`flex-1 font-medium py-2 px-3 rounded disabled:opacity-50 text-sm ${
                  actionMode === 'confirm' ? 'bg-green-600 hover:bg-green-700' :
                  actionMode === 'contest' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-blue-600 hover:bg-blue-700'
                } text-white`}
                disabled={isSubmitting || (!selectedStationId && !preselectedStation)}
              >
                {isSubmitting ? '⏳' :
                 actionMode === 'confirm' ? '✅' :
                 actionMode === 'contest' ? '❌' :
                 '📝'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickReportModal;